import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Header } from '../types';
import { HEADER_COLUMNS } from '../constants/columns';
import { formatDate } from '../utils/dateFormatter';
import ExcelFilter from './ExcelFilter';

interface HeaderTableProps {
  headers: Header[];
  onHeaderClick: (header: Header) => void;
  selectedHeader?: Header;
  isDarkTheme?: boolean;
  onHeaderUpdate?: (headerId: string, field: string, value: string) => void;
  radSites?: string[];
  radSitesLoading?: boolean;
  onLoadRadSites?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
}

const HeaderTable: React.FC<HeaderTableProps> = ({ 
  headers, 
  onHeaderClick, 
  selectedHeader, 
  isDarkTheme, 
  onHeaderUpdate,
  radSites = [],
  radSitesLoading = false,
  onLoadRadSites,
  onLoadMore,
  hasMore = false,
  loading = false
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({
    'Request Number': [],
    'From Site': [],
    'To Site': []
  });

  const filteredHeaders = useMemo(() => {
    return headers.filter(header => {
      const requestNumberMatch = filters['Request Number'].length === 0 || 
        filters['Request Number'].includes(header.requestNumber);
      const fromSiteMatch = filters['From Site'].length === 0 || 
        filters['From Site'].includes(header.fromSite);
      const toSiteMatch = filters['To Site'].length === 0 || 
        filters['To Site'].includes(header.toSite);
      
      return requestNumberMatch && fromSiteMatch && toSiteMatch;
    });
  }, [headers, filters]);

  const handleFilterChange = (column: string, selectedValues: string[]) => {
    setFilters(prev => ({
      ...prev,
      [column]: selectedValues
    }));
  };

  // Handle scroll for infinite loading
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current || !hasMore || loading || !onLoadMore) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        onLoadMore();
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, loading, onLoadMore]);

  const getColumnValues = (column: string): string[] => {
    const fieldMap: Record<string, keyof Header> = {
      'Request Number': 'requestNumber',
      'From Site': 'fromSite',
      'To Site': 'toSite'
    };
    return headers.map(header => String(header[fieldMap[column]] || ''));
  };
  const getHeaderValue = (header: Header, column: string): string => {
    const fieldMap: Record<string, keyof Header> = {
      'Request Number': 'requestNumber',
      'From Site': 'fromSite',
      'To Site': 'toSite',
      'Type': 'type',
      'Status': 'status',
      'Creation Date': 'creationDate',
      'Tracking Number': 'trackingNumber',
      'Tracking Status': 'trackingStatus',
      'Expected Delivery Date': 'expectedDeliveryDate',
      'Delivered Date': 'deliveredDate',
      'Region': 'region'
    };
    const value = header[fieldMap[column]] || '';
    const dateColumns = ['Creation Date', 'Expected Delivery Date', 'Delivered Date'];
    
    if (dateColumns.includes(column) && value) {
      return formatDate(String(value));
    }
    
    return String(value);
  };

  return (
    <div style={{ marginBottom: '10px' }}>
      <style>
        {`
          .thick-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .thick-scrollbar::-webkit-scrollbar-track {
            background: ${isDarkTheme ? '#2a2a2a' : '#f1f1f1'};
          }
          .thick-scrollbar::-webkit-scrollbar-thumb {
            background: ${isDarkTheme ? '#555' : '#888'};
            border-radius: 8px;
          }
          .thick-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${isDarkTheme ? '#777' : '#555'};
          }
        `}
      </style>
      <div 
        ref={scrollContainerRef}
        style={{ 
          maxHeight: '280px', 
          overflowY: 'auto', 
          border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`
        }}
        className="thick-scrollbar"
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: isDarkTheme ? '#444' : '#f5f5f5' }}>
            {HEADER_COLUMNS.map((column) => {
              const hasFilter = ['Request Number', 'From Site', 'To Site'].includes(column);
              return (
                <th key={column} style={{ 
                  padding: '8px', 
                  border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, 
                  textAlign: 'left', 
                  color: isDarkTheme ? '#fff' : '#000', 
                  fontSize: '13px', 
                  position: 'sticky', 
                  top: '0', 
                  backgroundColor: isDarkTheme ? '#444' : '#f5f5f5', 
                  zIndex: 10,
                  width: column === 'To Site' ? '200px' : 'auto',
                  minWidth: column === 'To Site' ? '200px' : 'auto'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{column}</span>
                    {hasFilter && (
                      <ExcelFilter
                        column={column}
                        values={getColumnValues(column)}
                        selectedValues={filters[column]}
                        onFilterChange={handleFilterChange}
                        isDarkTheme={isDarkTheme}
                      />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filteredHeaders.length === 0 ? (
            <tr style={{ backgroundColor: isDarkTheme ? '#2a2a2a' : 'white', color: isDarkTheme ? '#fff' : '#000' }}>
              {HEADER_COLUMNS.map((column) => (
                <td key={column} style={{ padding: '8px', border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, color: isDarkTheme ? '#fff' : '#000', fontSize: '12px' }}>
                  
                </td>
              ))}
            </tr>
          ) : (
            filteredHeaders.map((header, index) => (
              <tr
                key={index}
                onClick={() => onHeaderClick(header)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: selectedHeader?.requestNumber === header.requestNumber 
                    ? (isDarkTheme ? '#0d47a1' : '#e3f2fd') 
                    : (isDarkTheme ? '#2a2a2a' : 'white'),
                  color: isDarkTheme ? '#fff' : '#000'
                }}
              >
                {HEADER_COLUMNS.map((column) => {
                  const cellValue = getHeaderValue(header, column);
                  const isEditable = column === 'To Site';
                  
                  return (
                    <td key={column} style={{ 
                      padding: '4px', 
                      border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, 
                      color: isDarkTheme ? '#fff' : '#000', 
                      fontSize: '12px',
                      width: column === 'To Site' ? '150px' : 'auto',
                      minWidth: column === 'To Site' ? '150px' : 'auto'
                    }}>
                      {isEditable && onHeaderUpdate ? (
                        <select
                          value={cellValue}
                          onChange={(e) => onHeaderUpdate(header.requestNumber, column, e.target.value)}
                          onFocus={onLoadRadSites}
                          style={{
                            width: '100%',
                            padding: '0px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: 'inherit',
                            fontSize: '12px',
                            boxSizing: 'border-box',
                            outline: 'none'
                          }}
                        >
                          {cellValue && !radSites.includes(cellValue) && (
                            <option value={cellValue}>{cellValue}</option>
                          )}
                          <option value="">Select site...</option>
                          {radSitesLoading ? (
                            <option disabled>Loading RAD sites...</option>
                          ) : (
                            radSites.map((site, index) => (
                              <option key={index} value={site}>
                                {site}
                              </option>
                            ))
                          )}
                        </select>
                      ) : (
                        <div style={{ minHeight: '16px', padding: '0px' }}>{cellValue}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          )}

        </tbody>
        </table>
        {loading && (
          <>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
            <div style={{ textAlign: 'center', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid #007bff",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}
              />
              <span style={{ color: isDarkTheme ? '#fff' : '#000' }}>Loading more records...</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HeaderTable;